import {
  type ChangeEvent,
  Fragment,
  useCallback,
  useRef,
  useState,
} from 'react';

import { Icon } from '@/app/components/icons';
import { IconTune } from '@/app/components/icons/photo-image/icon-tune';
import { IconExpandMore } from '@/app/components/icons/ui-actions/icon-expand-more';
import { Container } from '@/app/components/layouts/container';
import { PopoverOptionsProps } from '@/app/components/shared/dealer/components/shared/filter-options/popover-options';
import { PopoverOptionsDesktop } from '@/app/components/shared/dealer/components/shared/filter-options/popover-options/desktop';
import { PopoverOptionsMobile } from '@/app/components/shared/dealer/components/shared/filter-options/popover-options/mobile';
import { Tag } from '@/app/components/shared/dealer/components/shared/tag';
import { useDealerSearchKey } from '@/app/components/shared/dealer/hooks/useDealerSearchKey';
import styles from '@/app/components/shared/dealer/styles.module.css';
import {
  ADVANCED_SEARCH,
  type AdvancedSearch,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import { ICON_DEALER_TYPE_MAP } from '@/app/components/shared/dealer/utils/dealer-icons';
import { EVENT_NAME } from '@/app/components/shared/dealer/utils/tracking';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';

export interface FilterOptionsProps {
  className?: string;
  isOpenFilter?: boolean;
  hideFilter?: boolean;
  selectedOptions?: Set<string>;
  onSetNewOptions?: (options: string[]) => void;
  onSelectOptions?: (ev: ChangeEvent<HTMLInputElement>) => void;
  onReset?: (type?: AdvancedSearch) => void;
  onSearch?: () => void;
  onToggleFilter?: () => void;
}

export const FilterOptions = ({
  className,
  isOpenFilter = false,
  hideFilter = false,
  selectedOptions = new Set(),
  onToggleFilter,
  onSetNewOptions,
  onSelectOptions,
  onReset,
  onSearch,
}: FilterOptionsProps) => {
  const isDesktop = useMediaQuery('(min-width: 1280px)');
  const { setToggleSearchKey } = useDealerSearchKey();
  const filterOptionsRef = useRef<HTMLDivElement | null>(null);
  const toggleOptionsRefs = useRef<Record<string, HTMLLabelElement | null>>({});

  const [prevSelectedOptions, setPrevSelectedOptions] = useState<Set<string>>(
    new Set()
  );
  const [idOpenOptions, setIdOpenOptions] = useState<string>('');

  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  const setToggleOptionsRef =
    (id: string) => (element: HTMLLabelElement | null) => {
      toggleOptionsRefs.current[id] = element;
    };

  const onToggleSearchFilter = () => {
    safeCall(onToggleFilter);
  };

  const handleSelectType = (
    event: ChangeEvent<HTMLInputElement>,
    hasParams = true
  ) => {
    safeCall(onSelectOptions, event);

    if (hasParams) {
      const { name, checked } = event.target;
      setToggleSearchKey(name, checked);
    }
  };

  const handleCloseOptions = () => {
    setIdOpenOptions('');
    setIsConfirmed(false);
  };

  const toggleOptions = (id: string) => {
    if (isOpenFilter) {
      safeCall(onToggleFilter);
    }

    const isClose = idOpenOptions === id;

    if (isClose) {
      if (!isConfirmed) {
        safeCall(onSetNewOptions, Array.from(prevSelectedOptions));
      }
      return handleCloseOptions();
    }
    setPrevSelectedOptions(new Set(selectedOptions));
    setIdOpenOptions(id);
    setIsConfirmed(false);
  };

  const onFilterSearch = () => {
    safeCall(onSearch);
    handleCloseOptions();
  };

  const onResetFilterSearch = (type: AdvancedSearch) => {
    safeCall(onReset, type);
  };

  const countOptions = useCallback(
    (type: AdvancedSearch) => {
      return (
        type.options?.filter((option) => selectedOptions.has(option.name))
          ?.length || 0
      );
    },
    [selectedOptions]
  );

  const renderTagSearch = (
    type: AdvancedSearch,
    isSingleOption: boolean,
    index: number
  ) => {
    const count = countOptions(type);
    return (
      <Tag
        ref={setToggleOptionsRef(type.id)}
        key={type.id}
        name={type.id}
        className="px-3 xl:shadow-sm"
        checked={selectedOptions.has(type.id) || count > 0}
        iconBefore={
          ICON_DEALER_TYPE_MAP[type.icon as keyof typeof ICON_DEALER_TYPE_MAP]
        }
        iconAfter={
          !isSingleOption ? (
            <Icon
              icon={<IconExpandMore />}
              className={cn('size-5 duration-300', {
                'rotate-180': idOpenOptions === type.id,
              })}
            />
          ) : undefined
        }
        onChange={(event) =>
          isSingleOption ? handleSelectType(event) : toggleOptions(type.id)
        }
        {...createDataAttributes({
          test: `tag-${index}`,
        })}
        {...createDataGA({
          click: true,
          eventName: EVENT_NAME,
          group: 'components/shared/dealer/shared/filter-options',
          value: type.id,
          text: type.label,
        })}
      >
        {type.label}
      </Tag>
    );
  };

  const handleOnClosePopover = () => {
    setIdOpenOptions('');
  };

  const handleOnClosePopoverOutside = () => {
    setIdOpenOptions('');
    safeCall(onSetNewOptions, Array.from(prevSelectedOptions));
  };

  const basePopoverOptions: Omit<PopoverOptionsProps, 'type'> = {
    selectedOptions,
    onSelectOptions: handleSelectType,
    onSearch: onFilterSearch,
    onReset: onResetFilterSearch,
    onClose: handleOnClosePopover,
    onCloseOutside: handleOnClosePopoverOutside,
  };

  return (
    <>
      <div
        ref={filterOptionsRef}
        className={cn(
          'absolute top-16 mt-2 xl:top-0 xl:mt-3',
          'scrollbar-hidden w-full overflow-x-auto xl:w-auto xl:overflow-x-visible', // scroll wrapper
          styles.filterContainer,
          className,
          {
            'hidden xl:block': hideFilter,
          }
        )}
        {...createDataAttributes({ test: 'filter-options' })}
      >
        <Container
          size="mobile-only" // scroll content
          maxWidth="none"
          className="flex w-min gap-x-2 xl:size-fit xl:gap-x-4 xl:overflow-visible"
        >
          <Tag
            id="filter-search"
            iconBefore={<IconTune />}
            onChange={onToggleSearchFilter}
            checked={isOpenFilter}
            {...createDataAttributes({
              test: 'filter-search',
            })}
            {...createDataGA({
              click: true,
              eventName: EVENT_NAME,
              group: 'components/shared/dealer/shared/filter-options',
              value: 'filter-search',
              text: 'filter-search',
            })}
          >
            {(selectedOptions?.size || 0) > 0
              ? `(${selectedOptions?.size})`
              : ''}
          </Tag>

          {ADVANCED_SEARCH.map((type, index) => {
            const options = type.options || [];
            const isSingleOption = !!type.description || options.length === 0;
            const tagSearch = renderTagSearch(type, isSingleOption, index);

            const popoverBaseProps = {
              ...basePopoverOptions,
              type,
              isShow: idOpenOptions === type.id,
              countOptions: countOptions(type),
              toggleOptionsRef: {
                current: toggleOptionsRefs.current[type.id],
              },
            };

            if (isSingleOption) {
              return tagSearch;
            }

            if (isDesktop) {
              return (
                <PopoverOptionsDesktop
                  key={type.id}
                  {...popoverBaseProps}
                  className={popoverBaseProps.className}
                >
                  {tagSearch}
                </PopoverOptionsDesktop>
              );
            }

            return (
              <Fragment key={type.id}>
                {tagSearch}
                <PopoverOptionsMobile {...popoverBaseProps} />
              </Fragment>
            );
          })}
        </Container>
      </div>
    </>
  );
};
